import { Result1 } from '@mm-shared/types/Result1';
import { Objective1 } from '@mm-shared/types/Objective1';
import { filterItemsForInterval } from './statusUtils';

export interface CircleLayout {
  radius: number;
  grid: { rows: number; cols: number };
  padding: number;
}

export interface ColumnInfo {
  startDate: Date;
  endDate: Date;
  // Add other properties as needed
}

export function getResultCircleLayout(resultCount: number): CircleLayout {
  // Optimized sizing for 100x100 viewBox - aiming for 50-80% surface coverage
  // Bigger circles with proportionally reduced padding
  if (resultCount === 1) return { radius: 35, grid: { rows: 1, cols: 1 }, padding: 2 };
  if (resultCount <= 4) return { radius: 22, grid: { rows: 2, cols: 2 }, padding: 2 };
  if (resultCount <= 6) return { radius: 16, grid: { rows: 2, cols: 3 }, padding: 2 };
  if (resultCount <= 9) return { radius: 14, grid: { rows: 3, cols: 3 }, padding: 2 };
  if (resultCount <= 12) return { radius: 10, grid: { rows: 3, cols: 4 }, padding: 2 };
  if (resultCount <= 16) return { radius: 8, grid: { rows: 4, cols: 4 }, padding: 2 };
  if (resultCount <= 20) return { radius: 6, grid: { rows: 4, cols: 5 }, padding: 2 };
  console.warn(
    'Result count exceeds 20. More than 20 results in a single status cell is not supported.',
  );
  return { radius: 5, grid: { rows: 5, cols: 5 }, padding: 2 };
}

/**
 * Returns the color of the result circle based on the result and status code.
 *
 * Color codes:
 * - Green: The result was delivered.
 * - Gray: The result was planned, but not delivered.
 * - No other colors are supported. It gets confusing quickly trying to interpret
 *   how the color of a yellow or red result circle relates to the status report.
 *   Limiting results to delivered and not delivered, green and gray, makes it simple to
 *   understand and interpret.
 *
 * @param result - The result to get the color for.
 * @param statusCode - The status code of parent status cell.
 * @param enableFill - Whether to fill the circles or make them hollow (default: true).
 * @returns The color of the result circle or "none" if fill is disabled.
 */
export function getResultCircleColor(result: Result1, statusCode: string, enableFill: boolean = true): string {
  if (!enableFill) {
    return 'none';
  }
  
  if (result.delivered) {
    return result.isKeyResult ? 'var(--status-G)' : 'var(--status-g)';
  } else {
    return result.isKeyResult ? 'var(--status-M)' : 'var(--status-i)';
  }

  /*
  // TODO: The color coding below got complex fast. Let's try this with the simpler approach
  // above. If it works, we can remove the code below. If it doesn't, revisit the logic
  // below and see if we can simplify it. -Bryan 7/10/2025
  //
  // If deadline is in the future, show gray.
  const deadlineHasPassed = result.deadline && new Date(result.deadline) < new Date();
  if (!result.delivered && !deadlineHasPassed) {
    return result.isKeyResult ? 'var(--status-M)' : 'var(--status-i)';
  }

  // If deadline is in the past, show yellow or red.
  // Yellow if:
  // - The status code is 'p' (no status has been reported) and the result is not a key result
  // - The status code is 'g' or 'G', meaning a user has reported status and judged this to be a non-critical issue
  // Red if:
  // - The status code is 'p' (no status has been reported) and the result is a key result
  // - The status code is 'R'
  if (!result.delivered && deadlineHasPassed && statusCode === 'p') {
    return result.isKeyResult ? 'var(--status-R)' : 'var(--status-y)';
  }
  const isGreen = statusCode === 'g' || statusCode === 'G';
  if (!result.delivered && deadlineHasPassed && isGreen) {
    return result.isKeyResult ? 'var(--status-Y)' : 'var(--status-y)';
  }
  const isRed = statusCode === 'R' || statusCode === 'r';
  if (!result.delivered && deadlineHasPassed && isRed) {
    return result.isKeyResult ? 'var(--status-R)' : 'var(--status-r)';
  }

  return 'var(--status-y)';
  */
}

export function calculateCirclePositions(
  results: Result1[],
  cellDimensions: { width: number; height: number },
): Array<{ x: number; y: number }> {
  const layout = getResultCircleLayout(results.length);
  const { grid, padding } = layout;

  const availableWidth = cellDimensions.width - padding * 2;
  const availableHeight = cellDimensions.height - padding * 2;

  const cellWidth = availableWidth / grid.cols;
  const cellHeight = availableHeight / grid.rows;

  return results.map((_, index) => {
    const row = Math.floor(index / grid.cols);
    const col = index % grid.cols;

    return {
      x: padding + col * cellWidth + cellWidth / 2,
      y: padding + row * cellHeight + cellHeight / 2,
    };
  });
}

export function getResultCircleLayoutForReactFlow(resultCount: number): CircleLayout {
  // Smaller circles designed for React Flow 40x40px cells
  // Each circle needs to fit within the cell and be draggable
  if (resultCount === 1) return { radius: 12, grid: { rows: 1, cols: 1 }, padding: 4 };
  if (resultCount <= 4) return { radius: 8, grid: { rows: 2, cols: 2 }, padding: 2 };
  if (resultCount <= 6) return { radius: 6, grid: { rows: 2, cols: 3 }, padding: 2 };
  if (resultCount <= 9) return { radius: 5, grid: { rows: 3, cols: 3 }, padding: 1 };
  if (resultCount <= 12) return { radius: 4, grid: { rows: 3, cols: 4 }, padding: 1 };
  if (resultCount <= 16) return { radius: 3, grid: { rows: 4, cols: 4 }, padding: 1 };
  if (resultCount <= 20) return { radius: 3, grid: { rows: 4, cols: 5 }, padding: 1 };
  console.warn(
    'Result count exceeds 20. More than 20 results in a single status cell is not supported.',
  );
  return { radius: 2, grid: { rows: 5, cols: 5 }, padding: 1 };
}

export function getRelativeCirclePosition(
  resultIndex: number,
  totalResults: number,
  cellWidth: number,
  cellHeight: number,
): { x: number; y: number } {
  const layout = getResultCircleLayoutForReactFlow(totalResults);
  const { grid, padding } = layout;

  const availableWidth = cellWidth - padding * 2;
  const availableHeight = cellHeight - padding * 2;

  const cellWidthInGrid = availableWidth / grid.cols;
  const cellHeightInGrid = availableHeight / grid.rows;

  const row = Math.floor(resultIndex / grid.cols);
  const col = resultIndex % grid.cols;

  // Calculate position relative to the cell's top-left corner
  const x = padding + col * cellWidthInGrid + cellWidthInGrid / 2 - layout.radius;
  const y = padding + row * cellHeightInGrid + cellHeightInGrid / 2 - layout.radius;

  return { x, y };
}

export function groupResultsByCell(
  results: Result1[],
  objectives: Objective1[],
  columnsInfo: ColumnInfo[],
): Map<string, Result1[]> {
  const cellGroups = new Map<string, Result1[]>();

  objectives.forEach((objective) => {
    columnsInfo.forEach((column) => {
      const resultsForCell = filterItemsForInterval(
        objective.results,
        column.startDate,
        column.endDate,
      );

      if (resultsForCell.length > 0) {
        const cellKey = `${objective.uuid}-${column.startDate.toISOString()}`;
        cellGroups.set(cellKey, resultsForCell);
      }
    });
  });

  return cellGroups;
}
